import { useEffect } from 'react';

/**
 * Custom hook that allows you to listen for click events outside of a specific element
 * and trigger a handler function when such clicks occur.
 *
 * @param {Object} ref - A React ref object that points to the element you want to detect clicks outside of.
 * @param {Function} handler - A callback function that will be called when a click occurs outside of the referenced element.
 *
 * @example
 * const dropdownRef = useRef(null);
 * const handleCloseDropdown = () => {
 *   setIsOpen(false);
 * };
 *
 * useClickOutside(dropdownRef, handleCloseDropdown);
 */
export const useClickOutside = (ref, handler) => {
  useEffect(() => {
    if (ref && ref.current) {
      const listener = (event) => {
        // Do nothing if clicking ref's element or its descendants
        if (!ref.current || ref.current.contains(event.target)) {
          return;
        }
        // Call the handler
        handler(event);
      };

      document.addEventListener('click', listener);
      return () => {
        document.removeEventListener('click', listener);
      };
    }
  }, [ref, handler]);
};

/**
 * Custom hook to listen for clicks outside specified elements and invoke a handler.
 *
 * @param {Array<string>} classNames - An array of class names to monitor for outside clicks.
 * @param {Function} handler - Function to execute when a click occurs outside the specified elements.
 */
export const useClickOutsideWithSelector = (classNames, handler) => {
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click is not within any of the specified elements
      if (!classNames.some((className) => event.target.closest(`.${className}`))) {
        handler(event);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    // Cleanup event listener on component unmount
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };

    // Dependency array includes classNames and handler to reapply effect if they change
  }, [classNames, handler]);
};
